// The Authenticity Promise modal — ONE definition, shared by every surface that offers it.
//
// Currently opened from two places:
//   * Home/AuthenticityPromiseBar.jsx — the store-home strip
//   * pdp/PDPAuthenticityPromise.jsx  — the saree PDP badge
//
// It lives at Components/ root rather than under Home/ or pdp/ precisely because it belongs to
// neither. A promise whose terms differ by the screen you opened it from is not a promise, so the
// copy, the claim window, the cap and the support number exist here exactly once. Adding a third
// surface means importing this, never re-typing the paragraphs.
//
// Built on BottomSheet (Components/BottomSheet), the house modal — 12+ call sites and already
// responsive (lg:max-w-[480px] lg:mx-auto), so one component serves mobile and desktop.

import React from "react";
import { Trans, useTranslation } from "react-i18next";
import BottomSheet from "./BottomSheet/BottomSheet";
// The app's existing number formatter — 10 digits become "92402 18436". Rendered as
// `+91 92402 18436`, the same shape NavDrawer/Username.jsx:34 uses for the logged-in shopper's
// own number. Deliberately reused rather than hand-formatting a literal here.
import { formatMobileNumber } from "../Utils/HelperFunctions";

// Support number for promise claims. BARE national digits, no dial code and no spaces — the same
// shape every other number is stored in here (user.number, store.mobileNumber), so the one
// formatter below can present it. Exported so a search finds the definition and its single use.
export const SUPPORT_NUMBER = "9240218436";

// India only, matching how the number is displayed everywhere else in the app.
const SUPPORT_DIAL_CODE = "91";

// The claim window and cap, as approved. Named rather than inlined so the body copy and the fine
// print cannot drift apart — they state the same 45 days twice.
const CLAIM_WINDOW_DAYS = 45;
const CLAIM_CAP = "₹2,000";

/**
 * @param {boolean}  sheet     open state (owned by the caller)
 * @param {Function} setSheet  setter, passed straight through to BottomSheet
 * @param {string}   brand     the store's display name, already resolved by the caller
 */
export default function AuthenticityPromiseSheet({ sheet, setSheet, brand }) {
  const { t } = useTranslation();
  return (
    /* 55, matching ShippingBar exactly — and the reason is not cosmetic.
       BottomSheet builds its height class by interpolating the prop into the class name. Tailwind
       never sees that assembled string, so it emits the rule ONLY if the identical class text
       happens to appear somewhere else in src. The prop therefore works by accident, per value:
       55 appears nowhere, so nothing is emitted and the sheet sizes to its content — which is why
       ShippingBar's opens exactly as tall as its text. 75 does appear (an unrelated legacy page),
       so it IS emitted, and this sheet was pinned to three-quarters of the viewport regardless of
       content. 60 — BottomSheet's own default — appears in three files, so omitting the prop is
       worse than passing 55, not better.
       ⚠️ DO NOT write the assembled class names out in a comment here. Tailwind scans raw file
       text, comments included: an earlier revision of this note spelled them out and thereby
       created the very rule it was warning about, forcing this sheet to a fixed height again.
       The real fix belongs in BottomSheet, shared by 12+ call sites and out of scope here — some
       of those pass values that ARE emitted and may be relying on that. */
    <BottomSheet sheet={sheet} setSheet={setSheet} height="55">
      <h3 className="text-[15px] font-[400] text-ink-900 mb-3">
        {t("authSheet.title", { brand })}
      </h3>

      <div className="text-[12px] leading-[1.55] text-ink-700 space-y-[9px]">
        <p>
          <Trans
            i18nKey="authSheet.verifyFour"
            components={[<b className="text-ink-900 font-[400]" />]}
          />
        </p>

        <p>
          <Trans
            i18nKey="authSheet.labClaim"
            values={{ days: CLAIM_WINDOW_DAYS, cap: CLAIM_CAP }}
            components={[<b className="text-ink-900 font-[400]" />]}
          />
        </p>

        <p>{t("authSheet.scope")}</p>
      </div>

      {/* Fine print: smaller, muted, and separated by a rule so it reads as terms rather than as a
          fourth body paragraph. This is the ONLY place "Wcommerce" appears on either surface. */}
      <p className="mt-[14px] pt-[10px] border-t border-wc-border text-[10.5px] leading-[1.5] text-ink-500">
        <Trans
          i18nKey="authSheet.finePrint"
          values={{
            brand,
            days: CLAIM_WINDOW_DAYS,
            phone: `+${SUPPORT_DIAL_CODE} ${formatMobileNumber(SUPPORT_NUMBER)}`,
          }}
          components={[<b className="font-[500] text-ink-500" />]}
        />
      </p>
    </BottomSheet>
  );
}
